import React, { useState, useEffect } from 'react';
import { Cog6ToothIcon, XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useTranslation } from "react-i18next";

const POINT_STYLES = [
  { value: 'circle', label: 'Circle', icon: '●' },
  { value: 'rect', label: 'Square', icon: '■' },
  { value: 'triangle', label: 'Triangle', icon: '▲' },
  { value: 'rectRot', label: 'Diamond', icon: '◆' },
  { value: 'star', label: 'Star', icon: '★' },
  { value: 'cross', label: 'Cross', icon: '✕' },
  { value: 'crossRot', label: 'Plus', icon: '+' }
];

const DEFAULT_SETTINGS = {
  locations: [
    { color: 'rgba(75, 192, 192, 1)', pointStyle: 'circle', pointSize: 4, lineWidth: 0 },
    { color: 'rgba(255, 99, 132, 1)', pointStyle: 'circle', pointSize: 4, lineWidth: 0 },
    { color: 'rgba(54, 162, 235, 1)', pointStyle: 'circle', pointSize: 4, lineWidth: 0 },
    { color: 'rgba(153, 102, 255, 1)', pointStyle: 'circle', pointSize: 4, lineWidth: 0 },
    { color: 'rgba(255, 159, 64, 1)', pointStyle: 'circle', pointSize: 4, lineWidth: 0 }
  ],
  sst: {
    color: 'rgba(239, 68, 68, 1)',
    pointStyle: 'circle',
    pointSize: 4,
    lineWidth: 0
  },
  chlorophyll: {
    color: 'rgb(12, 162, 12)',
    pointStyle: 'circle',
    pointSize: 4,
    lineWidth: 0
  },
  regulatoryLimits: {
    DSP: { color: 'rgba(255, 165, 0, 0.6)', lineWidth: 2 },
    PSP: { color: 'rgba(255, 0, 0, 0.6)', lineWidth: 2 }
  }
};

const GraphCustomizationPanel = ({ isOpen, onClose, onSettingsChange, locationNames = [] }) => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [activeTab, setActiveTab] = useState('locations');

  // Load settings from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('graphCustomizationSettings');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSettings(parsed);
        onSettingsChange(parsed);
      } catch (e) {
        console.error('Failed to load settings:', e);
      }
    }
  }, [onSettingsChange]);

  // Save settings to localStorage whenever they change
  const updateSettings = (newSettings) => {
    setSettings(newSettings);
    localStorage.setItem('graphCustomizationSettings', JSON.stringify(newSettings));
    onSettingsChange(newSettings);
  };

  const resetToDefaults = () => {
    updateSettings(DEFAULT_SETTINGS);
  };

  const updateLocationSetting = (index, field, value) => {
    const newSettings = { ...settings };
    if (!newSettings.locations[index]) {
      newSettings.locations[index] = { ...DEFAULT_SETTINGS.locations[0] };
    }
    newSettings.locations[index][field] = value;
    updateSettings(newSettings);
  };

  const updateDataTypeSetting = (dataType, field, value) => {
    const newSettings = { ...settings };
    newSettings[dataType][field] = value;
    updateSettings(newSettings);
  };

  const updateRegulatoryLimitSetting = (toxinType, field, value) => {
    const newSettings = { ...settings };
    newSettings.regulatoryLimits[toxinType][field] = value;
    updateSettings(newSettings);
  };

  const hexToRgba = (hex, alpha = 1) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const rgbaToHex = (rgba) => {
    const match = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (!match) return '#4BC0C0';
    const r = parseInt(match[1]).toString(16).padStart(2, '0');
    const g = parseInt(match[2]).toString(16).padStart(2, '0');
    const b = parseInt(match[3]).toString(16).padStart(2, '0');
    return `#${r}${g}${b}`;
  };

  const getAlpha = (rgba) => {
    const match = rgba.match(/rgba?\([\d\s,]+,?\s*([\d.]+)?\)/);
    return match && match[1] ? parseFloat(match[1]) : 1;
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-[100]"
        onClick={onClose}
      />
      
      {/* Panel */}
      <div className="fixed inset-y-0 right-0 w-full sm:w-96 bg-white shadow-2xl z-[101] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <Cog6ToothIcon className="w-6 h-6 text-gray-700" />
            <h2 className="text-xl font-semibold text-gray-900">{t('graph.customization.title')}</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={resetToDefaults}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title={t('graph.customization.reset')}
            >
              <ArrowPathIcon className="w-5 h-5 text-gray-600" />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <XMarkIcon className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 bg-gray-50">
          <div className="flex">
            <button
              onClick={() => setActiveTab('locations')}
              className={`flex-1 px-4 py-3 text-sm font-medium ${
                activeTab === 'locations'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-white'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {t('graph.customization.tabs.locations')}
            </button>
            <button
              onClick={() => setActiveTab('satellite')}
              className={`flex-1 px-4 py-3 text-sm font-medium ${
                activeTab === 'satellite'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-white'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {t('graph.customization.tabs.satellite')}
            </button>
            <button
              onClick={() => setActiveTab('limits')}
              className={`flex-1 px-4 py-3 text-sm font-medium ${
                activeTab === 'limits'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-white'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {t('graph.customization.tabs.limits')}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-6">
          {/* Locations Tab */}
          {activeTab === 'locations' && (
            <div className="space-y-6">
              <p className="text-sm text-gray-600">
                {t('graph.customization.locations_description')}
              </p>
              {[0, 1, 2, 3, 4].map((index) => {
                const locationName = locationNames[index] || t('graph.customization.location_default', { number: index + 1 });
                const locationSettings = settings.locations[index] || DEFAULT_SETTINGS.locations[index];
                
                return (
                  <div key={index} className="bg-gray-50 rounded-lg p-4 space-y-4">
                    <h3 className="font-medium text-gray-900">{locationName}</h3>
                    
                    {/* Color Picker */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('graph.customization.color')}
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={rgbaToHex(locationSettings.color)}
                          onChange={(e) => {
                            const alpha = getAlpha(locationSettings.color);
                            updateLocationSetting(index, 'color', hexToRgba(e.target.value, alpha));
                          }}
                          className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                        />
                        <div className="flex-1">
                          <label className="block text-xs text-gray-600 mb-1">{t('graph.customization.opacity')}</label>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.1"
                            value={getAlpha(locationSettings.color)}
                            onChange={(e) => {
                              const hex = rgbaToHex(locationSettings.color);
                              updateLocationSetting(index, 'color', hexToRgba(hex, parseFloat(e.target.value)));
                            }}
                            className="w-full"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Point Style */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('graph.customization.point_style')}
                      </label>
                      <div className="grid grid-cols-4 gap-2">
                        {POINT_STYLES.map((style) => (
                          <button
                            key={style.value}
                            onClick={() => updateLocationSetting(index, 'pointStyle', style.value)}
                            className={`p-3 text-2xl border rounded-md transition-all ${
                              locationSettings.pointStyle === style.value
                                ? 'border-blue-500 bg-blue-50 text-blue-600'
                                : 'border-gray-300 hover:border-gray-400'
                            }`}
                            title={style.label}
                          >
                            {style.icon}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Point Size */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('graph.customization.point_size')}: {locationSettings.pointSize}px
                      </label>
                      <input
                        type="range"
                        min="2"
                        max="12"
                        step="1"
                        value={locationSettings.pointSize}
                        onChange={(e) => updateLocationSetting(index, 'pointSize', parseInt(e.target.value))}
                        className="w-full"
                      />
                    </div>

                    {/* Line Width */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('graph.customization.line_width')}: {locationSettings.lineWidth}px
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="5"
                        step="1"
                        value={locationSettings.lineWidth}
                        onChange={(e) => updateLocationSetting(index, 'lineWidth', parseInt(e.target.value))}
                        className="w-full"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Satellite Data Tab */}
          {activeTab === 'satellite' && (
            <div className="space-y-6">
              <p className="text-sm text-gray-600">
                {t('graph.customization.satellite_description')}
              </p>

              {/* SST Settings */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                <h3 className="font-medium text-gray-900">{t('graph.customization.sst_title')}</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('graph.customization.color')}</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={rgbaToHex(settings.sst.color)}
                      onChange={(e) => {
                        const alpha = getAlpha(settings.sst.color);
                        updateDataTypeSetting('sst', 'color', hexToRgba(e.target.value, alpha));
                      }}
                      className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                    />
                    <div className="flex-1">
                      <label className="block text-xs text-gray-600 mb-1">{t('graph.customization.opacity')}</label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={getAlpha(settings.sst.color)}
                        onChange={(e) => {
                          const hex = rgbaToHex(settings.sst.color);
                          updateDataTypeSetting('sst', 'color', hexToRgba(hex, parseFloat(e.target.value)));
                        }}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('graph.customization.point_style')}</label>
                  <div className="grid grid-cols-4 gap-2">
                    {POINT_STYLES.map((style) => (
                      <button
                        key={style.value}
                        onClick={() => updateDataTypeSetting('sst', 'pointStyle', style.value)}
                        className={`p-3 text-2xl border rounded-md transition-all ${
                          settings.sst.pointStyle === style.value
                            ? 'border-red-500 bg-red-50 text-red-600'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                        title={style.label}
                      >
                        {style.icon}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('graph.customization.point_size')}: {settings.sst.pointSize}px
                  </label>
                  <input
                    type="range"
                    min="2"
                    max="12"
                    step="1"
                    value={settings.sst.pointSize}
                    onChange={(e) => updateDataTypeSetting('sst', 'pointSize', parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('graph.customization.line_width')}: {settings.sst.lineWidth}px
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="5"
                    step="1"
                    value={settings.sst.lineWidth}
                    onChange={(e) => updateDataTypeSetting('sst', 'lineWidth', parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>
              </div>

              {/* Chlorophyll Settings */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                <h3 className="font-medium text-gray-900">{t('graph.customization.chlorophyll_title')}</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('graph.customization.color')}</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={rgbaToHex(settings.chlorophyll.color)}
                      onChange={(e) => {
                        const alpha = getAlpha(settings.chlorophyll.color);
                        updateDataTypeSetting('chlorophyll', 'color', hexToRgba(e.target.value, alpha));
                      }}
                      className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                    />
                    <div className="flex-1">
                      <label className="block text-xs text-gray-600 mb-1">{t('graph.customization.opacity')}</label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={getAlpha(settings.chlorophyll.color)}
                        onChange={(e) => {
                          const hex = rgbaToHex(settings.chlorophyll.color);
                          updateDataTypeSetting('chlorophyll', 'color', hexToRgba(hex, parseFloat(e.target.value)));
                        }}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('graph.customization.point_style')}</label>
                  <div className="grid grid-cols-4 gap-2">
                    {POINT_STYLES.map((style) => (
                      <button
                        key={style.value}
                        onClick={() => updateDataTypeSetting('chlorophyll', 'pointStyle', style.value)}
                        className={`p-3 text-2xl border rounded-md transition-all ${
                          settings.chlorophyll.pointStyle === style.value
                            ? 'border-green-500 bg-green-50 text-green-600'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                        title={style.label}
                      >
                        {style.icon}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('graph.customization.point_size')}: {settings.chlorophyll.pointSize}px
                  </label>
                  <input
                    type="range"
                    min="2"
                    max="12"
                    step="1"
                    value={settings.chlorophyll.pointSize}
                    onChange={(e) => updateDataTypeSetting('chlorophyll', 'pointSize', parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('graph.customization.line_width')}: {settings.chlorophyll.lineWidth}px
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="5"
                    step="1"
                    value={settings.chlorophyll.lineWidth}
                    onChange={(e) => updateDataTypeSetting('chlorophyll', 'lineWidth', parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Regulatory Limits Tab */}
          {activeTab === 'limits' && (
            <div className="space-y-6">
              <p className="text-sm text-gray-600">
                {t('graph.customization.limits_description')}
              </p>

              {/* DSP Limit */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                <h3 className="font-medium text-gray-900">{t('graph.customization.dsp_limit')}</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('graph.customization.color')}</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={rgbaToHex(settings.regulatoryLimits.DSP.color)}
                      onChange={(e) => {
                        const alpha = getAlpha(settings.regulatoryLimits.DSP.color);
                        updateRegulatoryLimitSetting('DSP', 'color', hexToRgba(e.target.value, alpha));
                      }}
                      className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                    />
                    <div className="flex-1">
                      <label className="block text-xs text-gray-600 mb-1">{t('graph.customization.opacity')}</label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={getAlpha(settings.regulatoryLimits.DSP.color)}
                        onChange={(e) => {
                          const hex = rgbaToHex(settings.regulatoryLimits.DSP.color);
                          updateRegulatoryLimitSetting('DSP', 'color', hexToRgba(hex, parseFloat(e.target.value)));
                        }}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('graph.customization.line_width')}: {settings.regulatoryLimits.DSP.lineWidth}px
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    step="1"
                    value={settings.regulatoryLimits.DSP.lineWidth}
                    onChange={(e) => updateRegulatoryLimitSetting('DSP', 'lineWidth', parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>
              </div>

              {/* PSP Limit */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                <h3 className="font-medium text-gray-900">{t('graph.customization.psp_limit')}</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('graph.customization.color')}</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={rgbaToHex(settings.regulatoryLimits.PSP.color)}
                      onChange={(e) => {
                        const alpha = getAlpha(settings.regulatoryLimits.PSP.color);
                        updateRegulatoryLimitSetting('PSP', 'color', hexToRgba(e.target.value, alpha));
                      }}
                      className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                    />
                    <div className="flex-1">
                      <label className="block text-xs text-gray-600 mb-1">{t('graph.customization.opacity')}</label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={getAlpha(settings.regulatoryLimits.PSP.color)}
                        onChange={(e) => {
                          const hex = rgbaToHex(settings.regulatoryLimits.PSP.color);
                          updateRegulatoryLimitSetting('PSP', 'color', hexToRgba(hex, parseFloat(e.target.value)));
                        }}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('graph.customization.line_width')}: {settings.regulatoryLimits.PSP.lineWidth}px
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    step="1"
                    value={settings.regulatoryLimits.PSP.lineWidth}
                    onChange={(e) => updateRegulatoryLimitSetting('PSP', 'lineWidth', parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default GraphCustomizationPanel;


